import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client';
import type { CiblePassation } from '../../types';
import { useFenetreModale } from '../demande/useFenetreModale';

/**
 * « J'ai tout transmis » : un dernier regard avant d'envoyer la passation (DECISIONS §37). Rien n'est bloquant : si des éléments manquent, la fenêtre les liste
 * et propose, d'abord, de les compléter (un seul « Compléter », qui ramène au premier manque par-dessus la page : on ne quitte rien) ; « Transmettre quand même »
 * reste possible, en second. Le curseur n'arrive jamais sur l'envoi.
 */
export function FenetreTransmission({ demandeId, prenomRemplacant, jour, vous, prenomAbsent, remplacantEnAttente = false, onFermer, onTransmise, onCompleter }: {
  demandeId: number;
  /** Le prénom de la remplaçante (« Camille »), ou « Le/la remplaçant·e » quand il est inconnu. */
  prenomRemplacant: string;
  /** « jeudi » : le jour de la séance. */
  jour: string;
  /** La personne qui transmet est l'intervenant·e absent·e elle-même (« vous »), sinon la gestion qui transmet à sa place. */
  vous: boolean;
  prenomAbsent: string;
  /** La personne désignée n'a pas encore confirmé : elle lira la passation à sa confirmation (DECISIONS §38). */
  remplacantEnAttente?: boolean;
  onFermer: () => void;
  onTransmise: () => void;
  onCompleter: (cible: CiblePassation) => void;
}) {
  const [manques, setManques] = useState<{ texte: string; cible: CiblePassation }[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  // Pendant la vérification le curseur est sur « Retour » (jamais sur l'envoi) ; la vérification terminée, il va sur « Compléter » s'il manque quelque chose.
  const { ref } = useFenetreModale(onFermer, { focusInitial: () => document.getElementById('transmission-gauche') });

  function lire(textes: string[], cibles: CiblePassation[]) {
    setManques(textes.map((texte, i) => ({ texte, cible: cibles[i] ?? 'mots' })));
  }

  useEffect(() => {
    let annule = false;
    api.verifierPassation(demandeId)
      .then((v) => { if (!annule) lire(v.manques, v.cibles); })
      .catch((err) => { if (!annule) setErreur(err instanceof Error ? err.message : "La vérification n'a pas abouti. Réessayez."); });
    return () => { annule = true; };
  }, [demandeId]);

  const complet = manques != null && manques.length === 0;
  const incomplet = manques != null && manques.length > 0;
  useEffect(() => {
    // Il manque quelque chose : le curseur arrive sur « Compléter » (l'action sûre), pas sur « Transmettre quand même ».
    if (incomplet && document.activeElement?.closest('[role=dialog]')) document.getElementById('transmission-droite')?.focus();
  }, [incomplet]);

  async function transmettre() {
    setEnvoi(true);
    setErreur(null);
    try {
      await api.appliquerAction(demandeId, 'terminer_passation', { confirme_incomplet: (manques?.length ?? 0) > 0 });
      onTransmise();
    } catch (err) {
      // Quelqu'un a modifié la passation entre-temps : la liste à jour s'affiche, la personne décide de nouveau.
      if (err instanceof ApiError && Array.isArray(err.corps.manques)) lire(err.corps.manques as string[], (err.corps.cibles as CiblePassation[] | undefined) ?? []);
      setErreur(err instanceof Error ? err.message : "L'envoi n'a pas abouti. Réessayez.");
    } finally {
      setEnvoi(false);
    }
  }

  const complete = vous ? 'vous pourrez compléter plus tard' : `${prenomAbsent} pourra compléter plus tard`;
  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" ref={ref} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="transmission-titre" aria-describedby="transmission-corps">
        <div className="modal-header">
          <h3 className="modal-title" id="transmission-titre">Transmettre à {prenomRemplacant} ?</h3>
          <button type="button" className="close-btn" onClick={onFermer} aria-label="Fermer">✕</button>
        </div>
        <div className="modal-body" id="transmission-corps" aria-live="polite">
          {manques == null && !erreur && <div className="text-sm text-ink-3" role="status">Vérification…</div>}
          {complet && <p className="text-sm text-ink-2">Tout y est. {remplacantEnAttente ? `${prenomRemplacant} sera prévenu·e dès qu’il ou elle aura confirmé, et pourra alors tout lire avant ${jour}.` : `${prenomRemplacant} sera prévenu·e et pourra tout lire avant ${jour}.`}</p>}
          {incomplet && manques && (
            <>
              <p className="text-sm text-ink-1">Il manque encore :</p>
              <ul className="pg-manques">{manques.map((m) => <li key={m.texte}>{m.texte}</li>)}</ul>
              <p className="text-sm text-ink-2">Si c’est voulu, transmettez quand même : {complete}, {prenomRemplacant} verra la version à jour.</p>
            </>
          )}
          {erreur && <div className="text-xs text-err" role="alert">{erreur}</div>}
        </div>
        <div className="modal-footer">
          {/* Complet : « Retour » puis « Transmettre » (accent). Il manque quelque chose : « Transmettre quand même » (secondaire) puis « Compléter » (accent). */}
          <button id="transmission-gauche" type="button" className="btn" disabled={envoi} onClick={() => (incomplet ? void transmettre() : onFermer())}>
            {incomplet ? 'Transmettre quand même' : 'Retour'}
          </button>
          <button id="transmission-droite" type="button" className="btn btn-accent" disabled={manques == null || envoi}
            onClick={() => (incomplet && manques ? onCompleter(manques[0].cible) : void transmettre())}>
            {envoi ? <span className="spinner spinner-sm" role="status" aria-label="Envoi en cours" /> : incomplet ? 'Compléter' : 'Transmettre'}
          </button>
        </div>
      </div>
    </div>
  );
}
